import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export type Theme = 'earthy' | 'forest' | 'air' | 'sea' | 'midnight';
export type ColorMode = 'light' | 'dark';

interface ThemeColors {
	background: string;
	foreground: string;
	card: string;
	cardForeground: string;
	popover: string;
	popoverForeground: string;
	primary: string;
	primaryForeground: string;
	secondary: string;
	secondaryForeground: string;
	muted: string;
	mutedForeground: string;
	accent: string;
	accentForeground: string;
	destructive: string;
	destructiveForeground: string;
	border: string;
	input: string;
	ring: string;
	chart1: string;
	chart2: string;
	chart3: string;
	chart4: string;
	chart5: string;
}

interface ThemeState {
	theme: Theme;
	colorMode: ColorMode;
	setTheme: (theme: Theme) => void;
	setColorMode: (mode: ColorMode) => void;
	toggleColorMode: () => void;
}

// Theme color definitions
const THEME_COLORS: Record<Theme, { light: ThemeColors; dark: ThemeColors }> = {
	earthy: {
		light: {
			background: 'hsl(42, 28%, 88%)',
			foreground: 'hsl(89, 54%, 19%)',
			card: 'hsl(0 0% 100%)',
			cardForeground: 'hsl(88 15% 11%)',
			popover: 'hsl(0 0% 100%)',
			popoverForeground: 'hsl(88 15% 11%)',
			primary: 'hsl(0, 0%, 100%)',
			primaryForeground: 'hsl(88 15% 11%)',
			secondary: 'hsl(0 0% 96.1%)',
			secondaryForeground: 'hsl(88 15% 11%)',
			muted: 'hsl(0 0% 96.1%)',
			mutedForeground: 'hsl(88 15% 11% / 0.7)',
			accent: 'hsl(0 0% 96.1%)',
			accentForeground: 'hsl(88 15% 11%)',
			destructive: 'hsl(0 84.2% 60.2%)',
			destructiveForeground: 'hsl(0 0% 98%)',
			border: 'hsl(88 15% 11%)',
			input: 'hsl(0 0% 89.8%)',
			ring: 'hsl(0 0% 3.9%)',
			chart1: 'hsl(12 76% 61%)',
			chart2: 'hsl(173 58% 39%)',
			chart3: 'hsl(197 37% 24%)',
			chart4: 'hsl(43 74% 66%)',
			chart5: 'hsl(27 87% 67%)',
		},
		dark: {
			background: 'hsl(30, 15%, 8%)',
			foreground: 'hsl(0 0% 98%)',
			card: 'hsl(30, 15%, 12%)',
			cardForeground: 'hsl(0 0% 98%)',
			popover: 'hsl(30, 15%, 12%)',
			popoverForeground: 'hsl(0 0% 98%)',
			primary: 'hsl(27 87% 67%)',
			primaryForeground: 'hsl(30, 15%, 8%)',
			secondary: 'hsl(30, 15%, 18%)',
			secondaryForeground: 'hsl(0 0% 98%)',
			muted: 'hsl(30, 15%, 18%)',
			mutedForeground: 'hsl(0 0% 63.9%)',
			accent: 'hsl(30, 15%, 18%)',
			accentForeground: 'hsl(0 0% 98%)',
			destructive: 'hsl(0 62.8% 30.6%)',
			destructiveForeground: 'hsl(0 0% 98%)',
			border: 'hsl(30, 15%, 18%)',
			input: 'hsl(30, 15%, 18%)',
			ring: 'hsl(27 87% 67%)',
			chart1: 'hsl(27 87% 67%)',
			chart2: 'hsl(173 58% 39%)',
			chart3: 'hsl(43 74% 66%)',
			chart4: 'hsl(12 76% 61%)',
			chart5: 'hsl(197 37% 24%)',
		},
	},
	forest: {
		light: {
			background: 'hsl(134, 32%, 89%)',
			foreground: 'hsl(120, 60%, 15%)',
			card: 'hsl(0 0% 100%)',
			cardForeground: 'hsl(120, 60%, 15%)',
			popover: 'hsl(0 0% 100%)',
			popoverForeground: 'hsl(120, 60%, 15%)',
			primary: 'hsl(0, 0%, 100%)',
			primaryForeground: 'hsl(120, 60%, 15%)',
			secondary: 'hsl(0 0% 96.1%)',
			secondaryForeground: 'hsl(120, 60%, 15%)',
			muted: 'hsl(0 0% 96.1%)',
			mutedForeground: 'hsl(120, 60%, 15% / 0.7)',
			accent: 'hsl(0 0% 96.1%)',
			accentForeground: 'hsl(120, 60%, 15%)',
			destructive: 'hsl(0 84.2% 60.2%)',
			destructiveForeground: 'hsl(0 0% 98%)',
			border: 'hsl(120, 60%, 15%)',
			input: 'hsl(134, 32%, 85%)',
			ring: 'hsl(120, 100%, 25%)',
			chart1: 'hsl(120, 60%, 40%)',
			chart2: 'hsl(173 58% 39%)',
			chart3: 'hsl(88, 50%, 45%)',
			chart4: 'hsl(43 74% 66%)',
			chart5: 'hsl(27 87% 67%)',
		},
		dark: {
			background: 'hsl(120, 25%, 8%)',
			foreground: 'hsl(0 0% 98%)',
			card: 'hsl(120, 25%, 12%)',
			cardForeground: 'hsl(0 0% 98%)',
			popover: 'hsl(120, 25%, 12%)',
			popoverForeground: 'hsl(0 0% 98%)',
			primary: 'hsl(120, 100%, 50%)',
			primaryForeground: 'hsl(120, 25%, 8%)',
			secondary: 'hsl(120, 25%, 18%)',
			secondaryForeground: 'hsl(0 0% 98%)',
			muted: 'hsl(120, 25%, 18%)',
			mutedForeground: 'hsl(0 0% 63.9%)',
			accent: 'hsl(120, 25%, 18%)',
			accentForeground: 'hsl(0 0% 98%)',
			destructive: 'hsl(0 62.8% 30.6%)',
			destructiveForeground: 'hsl(0 0% 98%)',
			border: 'hsl(120, 25%, 18%)',
			input: 'hsl(120, 25%, 18%)',
			ring: 'hsl(120, 100%, 50%)',
			chart1: 'hsl(120, 100%, 50%)',
			chart2: 'hsl(173 58% 50%)',
			chart3: 'hsl(88, 70%, 55%)',
			chart4: 'hsl(43 84% 76%)',
			chart5: 'hsl(27 97% 77%)',
		},
	},
	air: {
		light: {
			background: 'hsl(0, 0%, 100%)',
			foreground: 'hsl(0, 0%, 9%)',
			card: 'hsl(0 0% 100%)',
			cardForeground: 'hsl(0, 0%, 9%)',
			popover: 'hsl(0 0% 100%)',
			popoverForeground: 'hsl(0, 0%, 9%)',
			primary: 'hsl(0, 0%, 9%)',
			primaryForeground: 'hsl(0, 0%, 100%)',
			secondary: 'hsl(0 0% 96.1%)',
			secondaryForeground: 'hsl(0, 0%, 9%)',
			muted: 'hsl(0 0% 96.1%)',
			mutedForeground: 'hsl(0, 0%, 45%)',
			accent: 'hsl(0 0% 96.1%)',
			accentForeground: 'hsl(0, 0%, 9%)',
			destructive: 'hsl(0 84.2% 60.2%)',
			destructiveForeground: 'hsl(0 0% 98%)',
			border: 'hsl(0 0% 89.8%)',
			input: 'hsl(0 0% 89.8%)',
			ring: 'hsl(0, 0%, 9%)',
			chart1: 'hsl(220, 70%, 50%)',
			chart2: 'hsl(160, 60%, 45%)',
			chart3: 'hsl(30, 80%, 55%)',
			chart4: 'hsl(280, 65%, 60%)',
			chart5: 'hsl(340, 75%, 55%)',
		},
		dark: {
			background: 'hsl(0, 0%, 9%)',
			foreground: 'hsl(0 0% 98%)',
			card: 'hsl(0, 0%, 14%)',
			cardForeground: 'hsl(0 0% 98%)',
			popover: 'hsl(0, 0%, 14%)',
			popoverForeground: 'hsl(0 0% 98%)',
			primary: 'hsl(0 0% 98%)',
			primaryForeground: 'hsl(0, 0%, 9%)',
			secondary: 'hsl(0, 0%, 20%)',
			secondaryForeground: 'hsl(0 0% 98%)',
			muted: 'hsl(0, 0%, 20%)',
			mutedForeground: 'hsl(0 0% 63.9%)',
			accent: 'hsl(0, 0%, 20%)',
			accentForeground: 'hsl(0 0% 98%)',
			destructive: 'hsl(0 62.8% 30.6%)',
			destructiveForeground: 'hsl(0 0% 98%)',
			border: 'hsl(0, 0%, 20%)',
			input: 'hsl(0, 0%, 20%)',
			ring: 'hsl(0 0% 98%)',
			chart1: 'hsl(220, 80%, 60%)',
			chart2: 'hsl(160, 70%, 55%)',
			chart3: 'hsl(30, 90%, 65%)',
			chart4: 'hsl(280, 75%, 70%)',
			chart5: 'hsl(340, 85%, 65%)',
		},
	},
	sea: {
		light: {
			background: 'hsl(174, 40%, 90%)',
			foreground: 'hsl(174, 80%, 12%)',
			card: 'hsl(0 0% 100%)',
			cardForeground: 'hsl(174, 80%, 12%)',
			popover: 'hsl(0 0% 100%)',
			popoverForeground: 'hsl(174, 80%, 12%)',
			primary: 'hsl(0, 0%, 100%)',
			primaryForeground: 'hsl(174, 80%, 12%)',
			secondary: 'hsl(0 0% 96.1%)',
			secondaryForeground: 'hsl(174, 80%, 12%)',
			muted: 'hsl(0 0% 96.1%)',
			mutedForeground: 'hsl(174, 80%, 12% / 0.7)',
			accent: 'hsl(0 0% 96.1%)',
			accentForeground: 'hsl(174, 80%, 12%)',
			destructive: 'hsl(0 84.2% 60.2%)',
			destructiveForeground: 'hsl(0 0% 98%)',
			border: 'hsl(174, 80%, 12%)',
			input: 'hsl(174, 40%, 86%)',
			ring: 'hsl(174, 100%, 35%)',
			chart1: 'hsl(174, 70%, 40%)',
			chart2: 'hsl(166, 60%, 40%)',
			chart3: 'hsl(190, 80%, 45%)',
			chart4: 'hsl(43 74% 66%)',
			chart5: 'hsl(27 87% 67%)',
		},
		dark: {
			background: 'hsl(174, 30%, 8%)',
			foreground: 'hsl(0 0% 98%)',
			card: 'hsl(174, 30%, 12%)',
			cardForeground: 'hsl(0 0% 98%)',
			popover: 'hsl(174, 30%, 12%)',
			popoverForeground: 'hsl(0 0% 98%)',
			primary: 'hsl(174, 100%, 50%)',
			primaryForeground: 'hsl(174, 30%, 8%)',
			secondary: 'hsl(174, 30%, 18%)',
			secondaryForeground: 'hsl(0 0% 98%)',
			muted: 'hsl(174, 30%, 18%)',
			mutedForeground: 'hsl(0 0% 63.9%)',
			accent: 'hsl(174, 30%, 18%)',
			accentForeground: 'hsl(0 0% 98%)',
			destructive: 'hsl(0 62.8% 30.6%)',
			destructiveForeground: 'hsl(0 0% 98%)',
			border: 'hsl(174, 30%, 18%)',
			input: 'hsl(174, 30%, 18%)',
			ring: 'hsl(174, 100%, 50%)',
			chart1: 'hsl(174, 100%, 50%)',
			chart2: 'hsl(166, 80%, 55%)',
			chart3: 'hsl(190, 100%, 55%)',
			chart4: 'hsl(43 94% 76%)',
			chart5: 'hsl(27 97% 77%)',
		},
	},
	midnight: {
		light: {
			background: 'hsl(220, 20%, 94%)',
			foreground: 'hsl(220, 40%, 15%)',
			card: 'hsl(0 0% 100%)',
			cardForeground: 'hsl(220, 40%, 15%)',
			popover: 'hsl(0 0% 100%)',
			popoverForeground: 'hsl(220, 40%, 15%)',
			primary: 'hsl(0, 0%, 100%)',
			primaryForeground: 'hsl(220, 40%, 15%)',
			secondary: 'hsl(0 0% 96.1%)',
			secondaryForeground: 'hsl(220, 40%, 15%)',
			muted: 'hsl(0 0% 96.1%)',
			mutedForeground: 'hsl(220, 40%, 15% / 0.7)',
			accent: 'hsl(0 0% 96.1%)',
			accentForeground: 'hsl(220, 40%, 15%)',
			destructive: 'hsl(0 84.2% 60.2%)',
			destructiveForeground: 'hsl(0 0% 98%)',
			border: 'hsl(220, 40%, 15%)',
			input: 'hsl(220, 20%, 90%)',
			ring: 'hsl(220, 80%, 45%)',
			chart1: 'hsl(220, 70%, 50%)',
			chart2: 'hsl(200, 70%, 50%)',
			chart3: 'hsl(240, 70%, 60%)',
			chart4: 'hsl(43 74% 66%)',
			chart5: 'hsl(27 87% 67%)',
		},
		dark: {
			background: 'hsl(220, 30%, 6%)',
			foreground: 'hsl(0 0% 98%)',
			card: 'hsl(220, 30%, 10%)',
			cardForeground: 'hsl(0 0% 98%)',
			popover: 'hsl(220, 30%, 10%)',
			popoverForeground: 'hsl(0 0% 98%)',
			primary: 'hsl(220, 100%, 60%)',
			primaryForeground: 'hsl(220, 30%, 6%)',
			secondary: 'hsl(220, 30%, 16%)',
			secondaryForeground: 'hsl(0 0% 98%)',
			muted: 'hsl(220, 30%, 16%)',
			mutedForeground: 'hsl(0 0% 63.9%)',
			accent: 'hsl(220, 30%, 16%)',
			accentForeground: 'hsl(0 0% 98%)',
			destructive: 'hsl(0 62.8% 30.6%)',
			destructiveForeground: 'hsl(0 0% 98%)',
			border: 'hsl(220, 30%, 16%)',
			input: 'hsl(220, 30%, 16%)',
			ring: 'hsl(220, 100%, 60%)',
			chart1: 'hsl(220, 100%, 60%)',
			chart2: 'hsl(200, 100%, 55%)',
			chart3: 'hsl(240, 100%, 70%)',
			chart4: 'hsl(43 94% 76%)',
			chart5: 'hsl(27 97% 77%)',
		},
	},
};

// Apply theme colors to CSS variables
const applyThemeColors = (colors: ThemeColors) => {
	const root = document.documentElement;
	root.style.setProperty('--background', colors.background);
	root.style.setProperty('--foreground', colors.foreground);
	root.style.setProperty('--card', colors.card);
	root.style.setProperty('--card-foreground', colors.cardForeground);
	root.style.setProperty('--popover', colors.popover);
	root.style.setProperty('--popover-foreground', colors.popoverForeground);
	root.style.setProperty('--primary', colors.primary);
	root.style.setProperty('--primary-foreground', colors.primaryForeground);
	root.style.setProperty('--secondary', colors.secondary);
	root.style.setProperty('--secondary-foreground', colors.secondaryForeground);
	root.style.setProperty('--muted', colors.muted);
	root.style.setProperty('--muted-foreground', colors.mutedForeground);
	root.style.setProperty('--accent', colors.accent);
	root.style.setProperty('--accent-foreground', colors.accentForeground);
	root.style.setProperty('--destructive', colors.destructive);
	root.style.setProperty('--destructive-foreground', colors.destructiveForeground);
	root.style.setProperty('--border', colors.border);
	root.style.setProperty('--input', colors.input);
	root.style.setProperty('--ring', colors.ring);
	root.style.setProperty('--chart-1', colors.chart1);
	root.style.setProperty('--chart-2', colors.chart2);
	root.style.setProperty('--chart-3', colors.chart3);
	root.style.setProperty('--chart-4', colors.chart4);
	root.style.setProperty('--chart-5', colors.chart5);
};

export const useThemeStore = create<ThemeState>()(
	persist(
		(set, get) => ({
			theme: 'earthy',
			colorMode: 'light',

			setTheme: (theme: Theme) => {
				set({ theme });
				const { colorMode } = get();
				const colors = THEME_COLORS[theme][colorMode];
				applyThemeColors(colors);
			},

			setColorMode: (mode: ColorMode) => {
				set({ colorMode: mode });
				const { theme } = get();
				const colors = THEME_COLORS[theme][mode];
				applyThemeColors(colors);

				// Update dark class on html element
				if (mode === 'dark') {
					document.documentElement.classList.add('dark');
				} else {
					document.documentElement.classList.remove('dark');
				}
			},

			toggleColorMode: () => {
				const { colorMode } = get();
				const newMode = colorMode === 'light' ? 'dark' : 'light';
				get().setColorMode(newMode);
			},
		}),
		{
			name: 'ekonavi-theme-storage',
			onRehydrateStorage: () => (state) => {
				if (state) {
					// Apply theme on initial load
					const colors = THEME_COLORS[state.theme][state.colorMode];
					applyThemeColors(colors);

					// Apply dark class if needed
					if (state.colorMode === 'dark') {
						document.documentElement.classList.add('dark');
					}
				}
			},
		}
	)
);
